import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useLanguage } from "../contexts/LanguageContext";
import {
  HelpCircle,
  X,
  FileText,
  ChevronRight,
  BookOpen,
  Shield,
  Loader2,
  Search,
  AlertOctagon,
  Quote,
} from "lucide-react";

/**
 * Contextual page-aware Help popover.
 *
 * Props:
 *   pageId    — string. Knowledge-base page key used to fetch hand-curated articles.
 *   grounded  — optional bool. When true, augments the popover with a Compass
 *               Grounding Q&A box (super-admin only) that routes through
 *               `POST /api/studio-plaz/grounded-answer`. Default behavior
 *               (article list + browse all) is preserved when not set.
 *               Self-hides the Q&A section if the API returns 401/403 so
 *               non-super-admin tenants never see a broken affordance.
 */
export function ContextualHelp({ pageId, grounded = false }) {
  const { language: lang, t } = useLanguage();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [articles, setArticles] = useState([]);

  // ── Grounded Q&A state ─────────────────────────────────────────────────
  const [groundedHidden, setGroundedHidden] = useState(false);
  const [q, setQ] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const fetchArticles = useCallback(async () => {
    if (!pageId) return;
    try {
      const r = await api.get(`/knowledge-base/for-page/${pageId}`, { params: { lang } });
      setArticles(r.data.articles || []);
    } catch { /* ignore */ }
  }, [pageId, lang]);

  useEffect(() => { if (open && articles.length === 0) fetchArticles(); }, [open, fetchArticles, articles.length]);

  const askCanon = async (e) => {
    e?.preventDefault();
    const query = q.trim();
    if (query.length < 3) return;
    setResult(null);
    setSubmitting(true);
    try {
      const r = await api.post("/studio-plaz/grounded-answer", {
        q: query,
        top_k: 4,
        surface: "contextual_help",
        page_id: pageId,
      });
      setResult(r.data);
    } catch (ex) {
      // Self-hide the entire grounded section on 401/403 (tenant users)
      if ([401, 403].includes(ex?.response?.status)) {
        setGroundedHidden(true);
      } else {
        setResult({
          refused: true,
          refusal_reason: ex?.response?.data?.detail || "Grounding call failed.",
          confidence: "low",
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const showGrounded = grounded && !groundedHidden;

  return (
    <>
      {/* Help trigger button — Sprint 941B: stacked above AiChat (which sits at bottom-5 right-5) */}
      <button
        onClick={() => setOpen(!open)}
        className="fixed bottom-[5.5rem] right-6 z-[100] w-10 h-10 rounded-full bg-[var(--app-accent)] text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all flex items-center justify-center"
        data-testid="contextual-help-btn"
        title={t("Help")}
      >
        {open ? <X className="w-4 h-4" /> : <HelpCircle className="w-5 h-5" />}
      </button>

      {/* Popover — repositioned above the lifted help button */}
      {open && (
        <div
          className={`fixed bottom-[9.5rem] right-6 z-[100] rounded-xl border border-[var(--app-border)] bg-[var(--app-bg)] shadow-2xl animate-in fade-in slide-in-from-bottom-2 duration-200 ${
            showGrounded ? "w-96" : "w-72"
          }`}
          data-testid="contextual-help-popover"
        >
          <div className="px-4 pt-4 pb-2 border-b border-[var(--app-border)] flex items-center justify-between">
            <div className="flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-[var(--app-accent)]" />
              <span className="text-sm font-semibold text-[var(--app-text)]">{t("Help")}</span>
            </div>
            {showGrounded && (
              <span
                className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded"
                style={{ background: "rgba(99,102,241,0.14)", color: "#a5b4fc" }}
                data-testid="contextual-help-grounded-badge"
                title="Grounded answers backed by the Compass canon"
              >
                <Shield className="w-2.5 h-2.5" /> G.4
              </span>
            )}
          </div>

          {/* Grounded Q&A section (super-admin opt-in) */}
          {showGrounded && (
            <div
              className="px-3 pt-3 pb-3 border-b border-[var(--app-border)]"
              data-testid="contextual-help-grounded-section"
            >
              <form onSubmit={askCanon} className="flex items-center gap-1.5">
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder={t("Ask the Compass canon…")}
                  className="flex-1 text-xs px-2 py-1.5 rounded border bg-transparent text-[var(--app-text)]"
                  style={{ borderColor: "var(--app-border)" }}
                  data-testid="contextual-help-grounded-input"
                />
                <button
                  type="submit"
                  disabled={submitting || q.trim().length < 3}
                  className="inline-flex items-center justify-center px-2 py-1.5 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-40"
                  data-testid="contextual-help-grounded-submit"
                  title={t("Ask")}
                >
                  {submitting ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Search className="w-3.5 h-3.5" />
                  )}
                </button>
              </form>

              {result && (
                <div
                  className="mt-2 p-2 rounded border text-[11px]"
                  style={{
                    borderColor: result.refused ? "rgba(244,63,94,0.4)" : "rgba(99,102,241,0.3)",
                    background: result.refused ? "rgba(244,63,94,0.04)" : "rgba(99,102,241,0.04)",
                  }}
                  data-testid={result.refused ? "contextual-help-grounded-refused" : "contextual-help-grounded-answer"}
                >
                  <div className="flex items-center gap-1 mb-1">
                    {result.refused ? (
                      <>
                        <AlertOctagon className="w-3 h-3 text-rose-400" />
                        <span className="text-[10px] uppercase tracking-wider font-semibold text-rose-300">
                          {t("Refused")}
                        </span>
                      </>
                    ) : (
                      <>
                        <Quote className="w-3 h-3 text-indigo-300" />
                        <span className="text-[10px] uppercase tracking-wider font-semibold text-indigo-300">
                          {t("Answer")} · {result.confidence}
                        </span>
                      </>
                    )}
                  </div>
                  <div className="text-[var(--app-text-secondary)] leading-relaxed">
                    {result.refused
                      ? result.refusal_reason || t("Insufficient grounding.")
                      : result.answer}
                  </div>
                  {!result.refused && (result.citations || []).length > 0 && (
                    <div className="mt-1.5 text-[10px] text-[var(--app-text-muted)]">
                      {result.citations.length} citation
                      {result.citations.length === 1 ? "" : "s"} · top score{" "}
                      {result.retrieval?.top_score?.toFixed?.(3)}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="p-2 max-h-64 overflow-y-auto">
            {articles.length === 0 ? (
              <p className="text-xs text-[var(--app-text-faint)] p-2 text-center">{t("No guides for this page yet.")}</p>
            ) : (
              articles.map(art => (
                <button
                  key={art.article_id}
                  onClick={() => { navigate(`/help?article=${art.article_id}`); setOpen(false); }}
                  className="w-full flex items-start gap-2.5 px-3 py-2.5 rounded-lg hover:bg-[var(--app-surface)] transition-colors text-left group"
                  data-testid={`help-link-${art.article_id}`}
                >
                  <FileText className="w-3.5 h-3.5 text-[var(--app-text-faint)] mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-[var(--app-text)] group-hover:text-[var(--app-accent)] transition-colors">{art.title}</p>
                    <p className="text-[10px] text-[var(--app-text-faint)] mt-0.5 line-clamp-1">{art.summary}</p>
                  </div>
                  <ChevronRight className="w-3 h-3 text-[var(--app-text-faint)] flex-shrink-0 mt-0.5" />
                </button>
              ))
            )}
          </div>

          {/* Browse all */}
          <div className="px-3 pb-3 pt-1 border-t border-[var(--app-border)]">
            <button
              onClick={() => { navigate("/help"); setOpen(false); }}
              className="w-full text-center text-xs text-[var(--app-accent)] hover:underline py-1.5"
              data-testid="help-browse-all"
            >
              {t("Browse all articles")} →
            </button>
          </div>
        </div>
      )}
    </>
  );
}
